import {StyledContainer, StyledMobileFilters, StyledTabletFilters} from './styles';
import Props, {LocalReduxActionsProps} from './typings';

import {Fragment, useEffect, useRef, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';

import {
  changeShipsToCountry,
  loadPriceBars,
  resetAllFilters,
  resetCategory,
  resetColor,
  resetFramed,
  resetPrice,
  resetPrints,
  resetShape,
  resetShipFrom,
  resetShipTo,
  resetSize,
  resetStyle,
  resetSubject,
  setActiveFilters,
  setCategory,
  setColor,
  setFramed,
  setFreeShipping,
  setOnSale,
  setPrice,
  setPrints,
  setShape,
  setShipFrom,
  setShipTo,
  setSize,
  setSort,
  setStyle,
  setSubject,
} from '../../../../redux/actions/filterSettingsActions';
import {selectFilterConfig} from '../../../../redux/reducers/filterConfigReducer';
import {
  selectFilterSettings,
  selectPriceBarsData,
  selectSearchSettings,
} from '../../../../redux/reducers/filterSettingsReducer';
import useMounted from '../../../hooks/use-mounted/use-mounted';
import configs from '../../../utils/configs';
import paramsHelper from '../../../utils/paramsHelper';
import ActionText from '../../atoms/ActionText/ActionText';
import Switch from '../../atoms/Switch/Switch';
import FilterDropdown from '../../molecules/Dropdowns/Filter/FilterDropdown';
import Category, {getCategoryTitle} from '../../molecules/Fragments/Filters/Category/Category';
import Colour, {getColourTiltle} from '../../molecules/Fragments/Filters/Colour/Colour';
import Orientation, {
  getOrientationTitle,
} from '../../molecules/Fragments/Filters/Orientation/Orientation';
import Price, {getPriceTitle} from '../../molecules/Fragments/Filters/Price/Price';
import ShipsFrom from '../../molecules/Fragments/Filters/ShipsFrom/ShipsFrom';
import Size, {getSizeTitle} from '../../molecules/Fragments/Filters/Size/Size';
import Style, {getStyleTitle} from '../../molecules/Fragments/Filters/Style/Style';
import Subject, {getSubjectTitle} from '../../molecules/Fragments/Filters/Subject/Subject';
import Sort from '../../molecules/Sort/Sort';

const localReduxActions = {
  set: {
    freeShipping: setFreeShipping,
    onSale: setOnSale,
    price: setPrice,
    category: setCategory,
    size: setSize,
    style: setStyle,
    subject: setSubject,
    color: setColor,
    shape: setShape,
    shipTo: setShipTo,
    changeShipTo: changeShipsToCountry,
    shipFrom: setShipFrom,
    prints: setPrints,
    framed: setFramed,
    sort: setSort,
    activeFilters: setActiveFilters,
  },
  reset: {
    price: resetPrice,
    category: resetCategory,
    size: resetSize,
    style: resetStyle,
    subject: resetSubject,
    color: resetColor,
    shape: resetShape,
    shipTo: resetShipTo,
    shipFrom: resetShipFrom,
    prints: resetPrints,
    framed: resetFramed,
    allFilters: resetAllFilters,
  },
};

const Filters = ({
  filters,
  sort,
  userSettings,
  searchPreviewCount,
  reduxActions,
  sortCallback,
  filtersCallback,
}: Props) => {
  // Hooks
  const hasMounted = useMounted();
  const dispatch = useDispatch();

  // Redux selectors
  const selectedFilters = useSelector(selectFilterSettings); // Filters selected by user, but not applied yet
  const appliedFilters = useSelector(selectSearchSettings); // Filters applied to search
  const priceBarsData = useSelector(selectPriceBarsData);
  const filterConfig = useSelector(selectFilterConfig);

  // Variables
  // const redux: LocalReduxActionsProps = {...localReduxActions, ...reduxActions};
  const redux: LocalReduxActionsProps = {
    set: {
      ...localReduxActions.set,
      ...reduxActions.set,
    },
    reset: {
      ...localReduxActions.reset,
      ...reduxActions.reset,
    },
    searchPreview: reduxActions.searchPreview,
    GA: reduxActions.GA,
  };
  const [filterErrors, setFilterErrors] = useState({
    size: false,
    price: false,
  });
  const [filterOnMobileIsOpen, setFilterOnMobileIsOpen] = useState(false); // TODO: get rid of it once new navigation is created
  const previewLoading = useRef(false);
  const previewController = useRef<AbortController>();
  const selectedFiltersRef = useRef(selectedFilters);
  const barsLoading = useRef(false);
  const barsController = useRef<AbortController>();
  const priceFilterOpen = useRef(false);
  const priceDataFetched = useRef(false);
  const previewCountFetched = useRef(false);
  const filtersOpen = useRef(false);

  useEffect(() => {
    return () => {
      cancelRequest(previewController.current, previewLoading.current);
      cancelRequest(barsController.current, barsLoading.current);
    };
  }, []);

  // Filter Search button preview
  useEffect(() => {
    if (!hasMounted || !filters) return;

    selectedFiltersRef.current = selectedFilters;
    cancelRequest(previewController.current, previewLoading.current);
    getPreviewCount();
  }, [
    selectedFilters.product_category,
    selectedFilters.size,
    selectedFilters.has_fine_art_prints,
    selectedFilters.price,
    selectedFilters.in_sale,
    selectedFilters.free_shipping,
    selectedFilters.category_type,
    selectedFilters.shape,
    selectedFilters.subject,
    selectedFilters.style,
    selectedFilters.colours,
    selectedFilters.ships_to,
  ]);

  // Load new bars on all filter changes apart from price range
  useEffect(() => {
    if (!hasMounted || !filters) return;

    selectedFiltersRef.current = selectedFilters;
    cancelRequest(barsController.current, barsLoading.current);
    getPriceBars();
  }, [
    selectedFilters.product_category,
    selectedFilters.size,
    selectedFilters.has_fine_art_prints,
    selectedFilters.in_sale,
    selectedFilters.free_shipping,
    selectedFilters.category_type,
    selectedFilters.style,
    selectedFilters.colours,
    selectedFilters.framed_or_ready_to_hang,
    selectedFilters.subject,
    selectedFilters.shape,
    selectedFilters.ships_to,
  ]);

  // Change shipping country if user changes their settings
  useEffect(() => {
    if (!hasMounted || !filters) return;

    const shipToCountry = appliedFilters.ships_to ?? selectedFilters.ships_to ?? null;

    if (shipToCountry && userSettings.country !== shipToCountry) {
      dispatch(redux.set.changeShipTo(userSettings.country));
    }
  }, [userSettings.country]);

  const clearAllFilters = () => {
    dispatch(redux.reset.allFilters());
    if (filtersCallback) filtersCallback();
    if (redux.GA.filterUsed) dispatch(redux.GA.filterUsed());
  };

  const applyFilters = () => {
    dispatch(redux.set.activeFilters());
    if (filtersCallback) filtersCallback();
    if (redux.GA.filterUsed) dispatch(redux.GA.filterUsed());
  };

  const getPriceBars = () => {
    if (barsLoading.current) return;
    if (!priceFilterOpen.current) {
      priceDataFetched.current = false;
      return;
    }

    barsController.current = new AbortController();
    barsLoading.current = true;
    priceDataFetched.current = true;

    const successCallbacks = [(data) => (barsLoading.current = false)];
    const errorCallbacks = [
      (error) => {
        barsLoading.current = false;

        if (error.message === 'canceled') {
          getPriceBars();
        }
      },
    ];
    const cleanupCallbacks = [];

    const params = paramsHelper(selectedFiltersRef.current)
      .build({
        ranges:
          '0,500,1000,1500,2000,2500,3000,3500,4000,4500,5000,5500,6000,6500,7000,7500,8000,8500,9000,9500',
      })
      .filter(['paginate', 'sort', 'limit'])
      .unwrapCategories()
      .sort(configs.apiParams).result;

    dispatch(
      loadPriceBars(
        params,
        successCallbacks,
        errorCallbacks,
        cleanupCallbacks,
        barsController.current.signal,
      ),
    );
  };

  const getPreviewCount = () => {
    if (previewLoading.current || !redux.searchPreview) return;
    if (!filtersOpen.current) {
      previewCountFetched.current = false;
      return;
    }

    previewController.current = new AbortController();
    previewLoading.current = true;
    previewCountFetched.current = true;

    const successCallbacks = [(data) => (previewLoading.current = false)];
    const errorCallbacks = [
      (error) => {
        previewLoading.current = false;

        if (error.message === 'canceled') {
          getPreviewCount();
        }
      },
    ];
    const cleanupCallbacks = [];
    const params = paramsHelper(selectedFiltersRef.current)
      .build()
      .filter(['paginate', 'limit'])
      .unwrapCategories()
      .sort(configs.apiParams).result;

    dispatch(
      redux.searchPreview(
        params,
        successCallbacks,
        errorCallbacks,
        cleanupCallbacks,
        previewController.current.signal,
      ),
    );
  };

  const cancelRequest = (controller, loading) => {
    if (loading && controller) controller.abort();
  };

  const priceFilter = (standalone, collapsible, defaultCollapsed) => (
    <Price
      currency={userSettings.currencySymbol}
      priceBars={priceBarsData}
      minVal={selectedFilters.price.price_min}
      maxVal={selectedFilters.price.price_max}
      onSale={selectedFilters.in_sale ? true : false}
      freeShipping={selectedFilters.free_shipping ? true : false}
      freeShippingCallback={(checked) =>
        dispatch(redux.set.freeShipping(checked, userSettings.country))
      }
      onSaleCallback={(data) => dispatch(redux.set.onSale(data))}
      priceRangeCallback={(data) => dispatch(redux.set.price(data))}
      errorCallback={(hasError) => setFilterErrors({...filterErrors, price: hasError})}
      collapsible={standalone ? false : collapsible}
      defaultCollapsed={defaultCollapsed}
    />
  );

  const categoryFilter = (standalone, collapsible, defaultCollapsed) => (
    <Category
      selected={{
        categories: selectedFilters.product_category,
        types: selectedFilters.category_type,
      }}
      categoryCallback={(data) => dispatch(redux.set.category(data))}
      collapsible={standalone ? false : collapsible}
      defaultCollapsed={defaultCollapsed}
    />
  );

  const sizeFilter = (standalone, collapsible, defaultCollapsed) => (
    <Size
      size={selectedFilters.size.size_}
      widthMin={selectedFilters.size.width_min}
      widthMax={selectedFilters.size.width_max}
      heightMin={selectedFilters.size.height_min}
      heightMax={selectedFilters.size.height_max}
      displaySizeUnits={userSettings.displayUnits}
      sizeCallback={(data) => dispatch(redux.set.size(data))}
      errorCallback={(hasError) => setFilterErrors({...filterErrors, size: hasError})}
      collapsible={standalone ? false : collapsible}
      defaultCollapsed={defaultCollapsed}
    />
  );

  const styleFilter = (standalone, collapsible, defaultCollapsed) => (
    <Style
      selected={selectedFilters.style}
      styleCallback={(data) => dispatch(redux.set.style(data))}
      collapsible={standalone ? false : collapsible}
      defaultCollapsed={defaultCollapsed}
    />
  );

  const subjectFilter = (standalone, collapsible, defaultCollapsed) => (
    <Subject
      selected={selectedFilters.subject.map((name) => ({value: name}))}
      subjectCallback={(data) => dispatch(redux.set.subject(data))}
      collapsible={standalone ? false : collapsible}
      defaultCollapsed={defaultCollapsed}
    />
  );

  const colourFilter = (standalone, collapsible, defaultCollapsed) => (
    <Colour
      selected={selectedFilters.colours}
      colourCallback={(data) => dispatch(redux.set.color(data))}
      collapsible={standalone ? false : collapsible}
      defaultCollapsed={defaultCollapsed}
    />
  );

  const orientationFilter = (standalone, collapsible, defaultCollapsed) => (
    <Orientation
      selected={selectedFilters.shape}
      orientationCallback={(data) => dispatch(redux.set.shape(data))}
      collapsible={standalone ? false : collapsible}
      defaultCollapsed={defaultCollapsed}
    />
  );

  const shipToFilter = () => (
    <Switch
      title="Ships to my country"
      subtitle="Show only artworks that are available to be shipped to my country."
      checked={selectedFilters.ships_to === userSettings.country}
      callback={(checked) => dispatch(redux.set.shipTo(checked ? userSettings.country : null))}
    />
  );

  const shipFromFilter = () => (
    <ShipsFrom
      selected={selectedFilters.ships_from}
      callback={(countryCode) => dispatch(redux.set.shipFrom(countryCode))}
    />
  );

  const printsFilter = () => (
    <Switch
      title="Prints available"
      subtitle="Show only artworks that have prints available."
      checked={selectedFilters.has_fine_art_prints}
      callback={(checked) => dispatch(redux.set.prints(checked))}
    />
  );

  const framedFilter = () => (
    <Switch
      title="Framed or ready to hang"
      subtitle="Show only artworks framed or ready to hang."
      checked={selectedFilters.framed_or_ready_to_hang}
      callback={(checked) => dispatch(redux.set.framed(checked))}
    />
  );

  const togglePriceDropdown = (isOpen) => {
    filtersOpen.current = isOpen;
    priceFilterOpen.current = isOpen;
    if (isOpen && !priceDataFetched.current) {
      getPriceBars();
    }
    if (isOpen && !previewCountFetched.current) {
      getPreviewCount();
    }
  };

  const toggleFilterDropdown = (isOpen) => {
    filtersOpen.current = isOpen;
    if (isOpen && !previewCountFetched.current) {
      getPreviewCount();
    }
  };

  const filterData = {
    price: {
      titleFn: getPriceTitle(
        userSettings.currencySymbol,
        appliedFilters.price.price_min,
        appliedFilters.price.price_max,
        appliedFilters.in_sale === 'on',
        appliedFilters.free_shipping,
      ),
      clear: redux.reset.price(),
      component: priceFilter,
      toggleFn: togglePriceDropdown,
    },
    category: {
      titleFn: getCategoryTitle(appliedFilters.product_category, appliedFilters.category_type),
      clear: redux.reset.category(),
      component: categoryFilter,
      toggleFn: toggleFilterDropdown,
    },
    size: {
      titleFn: getSizeTitle(
        appliedFilters.size.size_,
        appliedFilters.size.width_max,
        appliedFilters.size.width_min,
        appliedFilters.size.height_max,
        appliedFilters.size.height_min,
        userSettings.displayUnits,
      ),
      clear: redux.reset.size(),
      component: sizeFilter,
      toggleFn: toggleFilterDropdown,
    },
    style: {
      titleFn: getStyleTitle(appliedFilters.style),
      clear: redux.reset.style(),
      component: styleFilter,
      toggleFn: toggleFilterDropdown,
    },
    subject: {
      titleFn: getSubjectTitle(appliedFilters.subject.map((name) => ({value: name}))),
      clear: redux.reset.subject(),
      component: subjectFilter,
      toggleFn: toggleFilterDropdown,
    },
    colour: {
      titleFn: getColourTiltle(appliedFilters.colours),
      clear: redux.reset.color(),
      component: colourFilter,
      toggleFn: toggleFilterDropdown,
    },
    orientation: {
      titleFn: getOrientationTitle(appliedFilters.shape),
      clear: redux.reset.shape(),
      component: orientationFilter,
      toggleFn: toggleFilterDropdown,
    },
    shipTo: {
      titleFn: {label: [], count: appliedFilters.ships_to ? 1 : 0},
      clear: redux.reset.shipTo(),
      component: shipToFilter,
      toggleFn: toggleFilterDropdown,
    },
    shipFrom: {
      titleFn: {label: [], count: appliedFilters.ships_from ? 1 : 0},
      clear: redux.reset.shipFrom(),
      component: shipFromFilter,
      toggleFn: toggleFilterDropdown,
    },
    prints: {
      titleFn: {label: [], count: appliedFilters.has_fine_art_prints ? 1 : 0},
      clear: redux.reset.prints(),
      component: printsFilter,
      toggleFn: toggleFilterDropdown,
    },
    framed: {
      titleFn: {label: [], count: appliedFilters.framed_or_ready_to_hang ? 1 : 0},
      clear: redux.reset.framed(),
      component: framedFilter,
      toggleFn: toggleFilterDropdown,
    },
  };

  const tabletFilterLayout = () => {
    const allPositions = Object.keys(filterConfig)
      .map((position) => Number(position))
      .sort((a, b) => a - b);
    const standalonePositions = allPositions.slice(0, 4);
    const embeddedPositions = allPositions.slice(4);

    return (
      <>
        {standalonePositions.map((position, i) => {
          return (
            <FilterDropdown
              key={i}
              defaultTitle={filterConfig[position].type}
              title={filterData[filterConfig[position].type].titleFn}
              resultCount={searchPreviewCount}
              searchFn={applyFilters}
              clearFn={() => {
                dispatch(filterData[filterConfig[position].type].clear);
                if (filtersCallback) filtersCallback();
                if (redux.GA.filterUsed) dispatch(redux.GA.filterUsed());
              }}
              hasErrors={filterErrors[filterConfig[position].type]}
              dropdownPosition={i < 3 ? 'left' : 'auto'}
              onOpen={filterData[filterConfig[position].type].toggleFn}
            >
              {filterData[filterConfig[position].type].component(
                true,
                filterConfig[position].collapsible,
                filterConfig[position].default_collapsed,
              )}
            </FilterDropdown>
          );
        })}

        {embeddedPositions.length > 0 && (
          <FilterDropdown
            defaultTitle="More filters"
            title={{
              label: [],
              count: embeddedPositions.reduce((a, b) => {
                const title = filterData[filterConfig[b].type].titleFn;
                return title ? a + title.count : a;
              }, 0),
            }}
            header="More filters"
            resultCount={searchPreviewCount}
            searchFn={applyFilters}
            clearFn={() => {
              embeddedPositions.forEach((position) => {
                dispatch(filterData[filterConfig[position].type].clear);
              });
              if (redux.GA.filterUsed) dispatch(redux.GA.filterUsed());
            }}
            hasErrors={false}
            dropdownPosition={standalonePositions.length < 3 ? 'left' : 'auto'}
            onOpen={toggleFilterDropdown}
          >
            {embeddedPositions.map((position, key) => (
              <Fragment key={key}>
                {filterData[filterConfig[position].type].component(
                  false,
                  filterConfig[position].collapsible,
                  filterConfig[position].default_collapsed,
                )}
              </Fragment>
            ))}
          </FilterDropdown>
        )}

        <ActionText
          fontSize={12}
          onClick={() => {
            clearAllFilters();
          }}
        >
          Clear all
        </ActionText>
      </>
    );
  };

  const mobileFilterLayout = () => {
    const allPositions = Object.keys(filterConfig)
      .map((position) => Number(position))
      .sort((a, b) => a - b);
    return (
      <FilterDropdown
        defaultTitle="Filters"
        title={{
          label: [],
          count: allPositions.reduce((a, b) => {
            const title = filterData[filterConfig[b].type].titleFn;
            return title ? a + title.count : a;
          }, 0),
        }}
        header="Filters"
        resultCount={searchPreviewCount}
        searchFn={applyFilters}
        clearFn={() => {
          clearAllFilters();
          if (redux.GA.filterUsed) dispatch(redux.GA.filterUsed());
        }}
        hasErrors={filterErrors.price || filterErrors.size}
        onOpen={(open) => {
          setFilterOnMobileIsOpen(open);
          togglePriceDropdown(open);
        }}
      >
        {allPositions.map((position, key) => (
          <Fragment key={key}>
            {filterData[filterConfig[position].type].component(
              false,
              filterConfig[position].collapsible,
              filterConfig[position].default_collapsed,
            )}
          </Fragment>
        ))}
      </FilterDropdown>
    );
  };

  return (
    <StyledContainer $mobileOpen={filterOnMobileIsOpen}>
      {filters && (
        <>
          <StyledTabletFilters>{tabletFilterLayout()}</StyledTabletFilters>

          <StyledMobileFilters>{mobileFilterLayout()}</StyledMobileFilters>
        </>
      )}

      {sort && (
        <Sort
          callback={(data) => {
            if (redux.GA.sortingUsed) dispatch(redux.GA.sortingUsed());
            dispatch(redux.set.sort(data));
            if (sortCallback) sortCallback();
          }}
          selected={selectedFilters?.sort ?? null}
          dropdownPosition="right"
        />
      )}
    </StyledContainer>
  );
};

export default Filters;
